import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';

import { leverageApi } from './api';
import RewardPayoutHistoryDialog from './components/RewardPayoutHistoryDialog';
import { useC1Stats, useC2Stats } from './hooks/useLeverageStats';
import type {
  LeverageC1StatItem,
  LeverageC2StatItem,
  LeverageCampaign,
  LeveragePrizePool,
  TranslateFn,
} from './types';
import { formatUsdc, getDefaultCampaign, getDefaultPool, getRemainingUsdc } from './utils';

const I18N_PREFIX = 'module.boldPredictionCampaign';

const tL = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

type TabValue = 'c1' | 'c2';

export const StatisticsPage = () => {
  const t = useTranslate();
  const [tab, setTab] = useState<TabValue>('c1');

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tL(t, 'stats.title')}
        links={[{ name: tL(t, 'title') }, { name: tL(t, 'stats.title') }]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tL(t, 'stats.description')}
      </Typography>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
        <Tab value="c1" label={tL(t, 'tab.c1')} />
        <Tab value="c2" label={tL(t, 'tab.c2')} />
      </Tabs>

      {tab === 'c1' ? <C1StatsPanel t={t} /> : <C2StatsPanel t={t} />}
    </DashboardContent>
  );
};

export default StatisticsPage;

// ============ Tab 1 ============

const C1StatsPanel = memo(({ t }: { t: TranslateFn }) => {
  const {
    items,
    total,
    totalLostAmountUsdc,
    totalVoucher,
    loading,
    error,
    search,
    limit,
    offset,
    setSearch,
    setLimit,
    setOffset,
  } = useC1Stats();

  const [campaign, setCampaign] = useState<LeverageCampaign>(getDefaultCampaign());
  useEffect(() => {
    let alive = true;
    leverageApi
      .getCampaign()
      .then((c) => {
        if (alive) setCampaign(c);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ '& > *': { flex: 1 } }}>
        <PoolStatCard
          label={tL(t, 'stats.c1.summary.totalUsers')}
          value={String(total)}
          icon="solar:users-group-rounded-bold-duotone"
          color="info.main"
          unit=""
        />
        <PoolStatCard
          label={tL(t, 'stats.c1.summary.totalLoss')}
          value={formatUsdc(totalLostAmountUsdc)}
          icon="solar:graph-down-bold-duotone"
          color="error.main"
        />
        <PoolStatCard
          label={tL(t, 'stats.c1.summary.voucherCap')}
          value={String(campaign.voucherCap || 0)}
          icon="solar:ticket-sale-bold-duotone"
          color="secondary.main"
          unit=""
        />
        <PoolStatCard
          label={tL(t, 'stats.c1.summary.totalVoucher')}
          value={String(totalVoucher)}
          icon="solar:ticket-bold-duotone"
          color="warning.main"
          unit=""
        />
      </Stack>

      <Card sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <TextField
              size="small"
              placeholder={tL(t, 'stats.searchC1Placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" width={18} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tL(t, 'stats.c1.col.user')}</TableCell>
                  <TableCell>{tL(t, 'stats.c1.col.eventId')}</TableCell>
                  <TableCell align="right">{tL(t, 'stats.c1.col.loss')}</TableCell>
                  <TableCell align="right">{tL(t, 'stats.c1.col.vouchers')}</TableCell>
                  <TableCell align="right">{tL(t, 'stats.c1.col.voucherCap')}</TableCell>
                  <TableCell align="right">{tL(t, 'stats.c1.col.multiplier')}</TableCell>
                  <TableCell>{tL(t, 'stats.c1.col.triggeredAt')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : items.length ? (
                  items.map((row, idx) => <C1Row key={`${row.userId}-${idx}`} row={row} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {tL(t, 'stats.empty')}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={Math.floor(offset / limit)}
            rowsPerPage={limit}
            rowsPerPageOptions={[25, 50, 100, 200]}
            onPageChange={(_, page) => setOffset(page * limit)}
            onRowsPerPageChange={(e) => {
              setLimit(Number(e.target.value));
              setOffset(0);
            }}
          />
        </Stack>
      </Card>
    </Stack>
  );
});

C1StatsPanel.displayName = 'C1StatsPanel';

const C1Row = memo(({ row }: { row: LeverageC1StatItem }) => (
  <TableRow hover>
    <TableCell>
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={600}>
          {row.userId}
        </Typography>
        <Typography variant="caption" fontFamily="monospace" color="text.secondary">
          {row.leverageWalletAddress}
        </Typography>
      </Stack>
    </TableCell>
    <TableCell>
      <Typography variant="caption" fontFamily="monospace">
        {row.triggerEventId || '-'}
      </Typography>
    </TableCell>
    <TableCell align="right">
      <Typography variant="body2" fontWeight={600}>
        {formatUsdc(row.lossUsdc)} USDC
      </Typography>
    </TableCell>
    <TableCell align="right">{row.voucherAmount}</TableCell>
    <TableCell align="right">{row.voucherCap}</TableCell>
    <TableCell align="right">{row.multiplier}x</TableCell>
    <TableCell>
      <Typography variant="caption" color="text.secondary">
        {row.triggeredAt ? dayjs(row.triggeredAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
      </Typography>
    </TableCell>
  </TableRow>
));

C1Row.displayName = 'C1Row';

// ============ Tab 2 ============

const C2StatsPanel = memo(({ t }: { t: TranslateFn }) => {
  const {
    items,
    total,
    totalTeamVolumeUsdc,
    totalRewardUsdcReceived,
    loading,
    error,
    search,
    limit,
    offset,
    setSearch,
    setLimit,
    setOffset,
  } = useC2Stats();

  const [pool, setPool] = useState<LeveragePrizePool>(getDefaultPool());
  useEffect(() => {
    let alive = true;
    leverageApi
      .getPool()
      .then((p) => {
        if (alive) setPool(p);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const remaining = getRemainingUsdc(pool);

  const [selectedCaptain, setSelectedCaptain] = useState<LeverageC2StatItem | null>(null);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ '& > *': { flex: 1 } }}>
        <PoolStatCard
          label={tL(t, 'stats.c2.pool.total')}
          value={formatUsdc(pool.totalUsdc)}
          icon="solar:wallet-money-bold-duotone"
          color="primary.main"
        />
        <PoolStatCard
          label={tL(t, 'stats.c2.pool.consumed')}
          value={formatUsdc(pool.consumedUsdc)}
          icon="solar:cart-large-2-bold-duotone"
          color="warning.main"
          suffix={tL(t, 'stats.c2.paid', {
            amount: formatUsdc(totalRewardUsdcReceived),
          })}
        />
        <PoolStatCard
          label={tL(t, 'stats.c2.pool.remaining')}
          value={formatUsdc(remaining)}
          icon="solar:safe-2-bold-duotone"
          color="success.main"
        />
        <PoolStatCard
          label={tL(t, 'stats.c2.pool.totalTeams')}
          value={String(total)}
          icon="solar:users-group-rounded-bold-duotone"
          color="info.main"
          unit=""
        />
        <PoolStatCard
          label={tL(t, 'stats.c2.pool.totalTeamVolume')}
          value={formatUsdc(totalTeamVolumeUsdc)}
          icon="solar:graph-up-bold-duotone"
          color="secondary.main"
        />
      </Stack>

      <Card sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <TextField
              size="small"
              placeholder={tL(t, 'stats.searchC2Placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" width={18} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tL(t, 'stats.c2.col.captain')}</TableCell>
                  <TableCell align="right">{tL(t, 'stats.c2.col.teamVolume')}</TableCell>
                  <TableCell align="right">{tL(t, 'stats.c2.col.members')}</TableCell>
                  <TableCell align="right">{tL(t, 'stats.c2.col.reward')}</TableCell>
                  <TableCell align="center">{tL(t, 'stats.c2.col.currentTier')}</TableCell>
                  <TableCell align="center">{tL(t, 'stats.c2.col.nextTier')}</TableCell>
                  <TableCell align="right">{tL(t, 'stats.c2.col.remaining')}</TableCell>
                  <TableCell>{tL(t, 'stats.c2.col.top1')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : items.length ? (
                  items.map((row, idx) => (
                    <C2Row
                      key={`${row.captainUserId}-${idx}`}
                      row={row}
                      t={t}
                      onClick={() => setSelectedCaptain(row)}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {tL(t, 'stats.empty')}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={Math.floor(offset / limit)}
            rowsPerPage={limit}
            rowsPerPageOptions={[25, 50, 100, 200]}
            onPageChange={(_, page) => setOffset(page * limit)}
            onRowsPerPageChange={(e) => {
              setLimit(Number(e.target.value));
              setOffset(0);
            }}
          />
        </Stack>
      </Card>

      <RewardPayoutHistoryDialog
        open={Boolean(selectedCaptain)}
        captainId={selectedCaptain?.captainUserId ?? ''}
        captainWallet={selectedCaptain?.captainLeverageWalletAddress}
        onClose={() => setSelectedCaptain(null)}
      />
    </Stack>
  );
});

C2StatsPanel.displayName = 'C2StatsPanel';

const PoolStatCard = memo(
  ({
    label,
    value,
    icon,
    color,
    suffix,
    unit = 'USDC',
  }: {
    label: string;
    value: string;
    icon: string;
    color: string;
    suffix?: string;
    unit?: string;
  }) => (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Iconify icon={icon} width={40} sx={{ color }} />
        <Stack spacing={0.25}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="h6">{unit ? `${value} ${unit}` : value}</Typography>
            {suffix ? (
              <Typography variant="caption" color="text.secondary">
                {suffix}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  )
);

PoolStatCard.displayName = 'PoolStatCard';

const C2Row = memo(
  ({ row, t, onClick }: { row: LeverageC2StatItem; t: TranslateFn; onClick?: () => void }) => {
    const rowEl = (
      <TableRow hover onClick={onClick} sx={onClick ? { cursor: 'pointer' } : undefined}>
        <TableCell>
          <Stack spacing={0.25}>
            <Typography variant="body2" fontWeight={600}>
              {row.captainUserId}
            </Typography>
            <Typography variant="caption" fontFamily="monospace" color="text.secondary">
              {row.captainLeverageWalletAddress}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>
            {formatUsdc(row.teamTotalVolumeUsdc)} USDC
          </Typography>
        </TableCell>
        <TableCell align="right">{row.memberCount}</TableCell>
        <TableCell align="right">
          <Stack spacing={0.25} alignItems="flex-end">
            <Typography variant="body2" fontWeight={600}>
              {formatUsdc(row.rewardUsdc)} USDC
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tL(t, 'stats.c2.paid', { amount: formatUsdc(row.rewardUsdcReceived) })}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <Chip label={`T${row.currentTier}`} color="success" size="small" />
        </TableCell>
        <TableCell align="center">
          {row.nextTier ? (
            <Chip label={`T${row.nextTier}`} color="info" size="small" />
          ) : (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{formatUsdc(row.remainingVolumeUsdc)} USDC</Typography>
        </TableCell>
        <TableCell>
          {row.top1 ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" fontFamily="monospace">
                {row.top1.leverageWalletAddress}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatUsdc(row.top1.cumulativeVolumeUsdc)} USDC
              </Typography>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          )}
        </TableCell>
      </TableRow>
    );
    if (!onClick) return rowEl;
    return (
      <Tooltip title={tL(t, 'stats.c2.rowHint')} placement="top" arrow followCursor>
        {rowEl}
      </Tooltip>
    );
  }
);

C2Row.displayName = 'C2Row';
